import React, { useEffect, useRef, useState } from "react";
import { Newspaper, CaretDown, CaretUp, ArrowSquareOut, ArrowsOutSimple } from "@phosphor-icons/react";
import { fetchNifcBriefing } from "../lib/api";
import { shareOrigin } from "../lib/shareOrigin";
import InlineShareButton from "./InlineShareButton";
import BriefPopout from "./BriefPopout";

const PL_COLORS = { 1: "#2A9D8F", 2: "#4C9AFF", 3: "#FFB347", 4: "#F2542D", 5: "#E63946" };

function StatChip({ label, value }) {
  if (value == null) return null;
  return (
    <div className="flex flex-col px-2 py-1 border border-tac-border bg-tac-surface rounded-sm min-w-[70px]">
      <span className="text-[10px] uppercase tracking-wider text-tac-faint">{label}</span>
      <span className="text-sm font-bold font-mono-tac text-tac-text">{value}</span>
    </div>
  );
}

export default function NifcBriefingCard() {
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(false);
  const [showWeather, setShowWeather] = useState(false);
  const [popout, setPopout] = useState(false);
  const sectionRef = useRef(null);

  useEffect(() => {
    let stop = false;
    fetchNifcBriefing()
      .then((d) => { if (!stop) setData(d); })
      .catch(() => {});
    return () => { stop = true; };
  }, []);

  useEffect(() => {
    if (!data?.available) return;
    if (new URLSearchParams(window.location.search).get("brief") === "nifc") {
      setPopout(true);
      setOpen(true);
      setTimeout(() => sectionRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 300);
    }
  }, [data?.available]);

  if (!data?.available) return null;
  const pl = data.preparedness_level;
  const plColor = PL_COLORS[pl] || "#8892A0";
  const wa = data.washington;

  const caption = [
    `🔥 NIFC Morning Briefing${data.date ? ` — ${data.date}` : ""}`,
    pl != null ? `National Preparedness Level ${pl}` : null,
    data.stats?.total_large_fires != null
      ? `Large fires: ${data.stats.total_large_fires}${data.stats.new_large_fires != null ? ` (${data.stats.new_large_fires} new)` : ""} · Active acres: ${data.stats.active_acres ?? "—"}`
      : null,
    wa?.fires?.length ? `Washington large fires: ${wa.fires_count || wa.fires.length}` : null,
    data.source_url ? `Full briefing: ${data.source_url}` : null,
    `Read it on the live fire map: ${shareOrigin()}/?brief=nifc`,
  ].filter(Boolean).join("\n");

  const body = (
    <div className="px-4 pb-3 space-y-3">
          {data.date && (
            <div className="text-[11px] font-mono-tac uppercase tracking-wider text-tac-faint">
              {data.date}
              {data.preparedness_as_of ? ` · PL as of ${data.preparedness_as_of}` : ""}
            </div>
          )}

          <div className="flex flex-wrap gap-1.5">
            <StatChip label="Large fires" value={data.stats?.total_large_fires} />
            <StatChip label="New" value={data.stats?.new_large_fires} />
            <StatChip label="Active acres" value={data.stats?.active_acres} />
            <StatChip label="YTD fires" value={data.stats?.ytd_fires} />
          </div>

          {(data.summary || []).slice(0, 3).map((p, i) => (
            <p key={i} className="text-[13px] leading-relaxed text-tac-dim">{p}</p>
          ))}

          {wa?.fires?.length > 0 && (
            <div>
              <div className="text-[11px] uppercase tracking-[0.15em] font-bold text-tac-primary mb-1">
                Washington Large Fires ({wa.fires_count || wa.fires.length})
              </div>
              <div className="border border-tac-border rounded-sm overflow-hidden">
                {wa.fires.map((f, i) => (
                  <div
                    key={i}
                    data-testid={`nifc-wa-fire-${i}`}
                    className={`flex items-center justify-between gap-2 px-2.5 py-1.5 text-[12px] ${i % 2 ? "bg-tac-surface/50" : ""}`}
                  >
                    <span className="font-bold text-tac-text truncate">
                      {f.new && <span className="text-tac-primary mr-1">NEW</span>}
                      {f.name}
                    </span>
                    <span className="font-mono-tac text-tac-dim shrink-0">
                      {f.size} ac · {f.pct_contained}%
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {data.weather && (
            <div>
              <button
                type="button"
                data-testid="nifc-weather-toggle"
                onClick={() => setShowWeather((v) => !v)}
                className="text-[11px] uppercase tracking-[0.15em] font-bold text-tac-dim hover:text-tac-primary transition-colors"
              >
                Weather Outlook {showWeather ? "▲" : "▼"}
              </button>
              {showWeather && (
                <p className="mt-1 text-[13px] leading-relaxed text-tac-dim">{data.weather}</p>
              )}
            </div>
          )}

          <div className="flex items-center gap-3">
            <a
              href={data.source_url}
              target="_blank"
              rel="noreferrer"
              data-testid="nifc-briefing-link"
              className="inline-flex items-center gap-1 text-[11px] uppercase tracking-wider font-bold text-tac-primary hover:underline"
            >
              Full briefing <ArrowSquareOut size={11} />
            </a>
            <a
              href={data.imsr_url}
              target="_blank"
              rel="noreferrer"
              data-testid="nifc-imsr-link"
              className="inline-flex items-center gap-1 text-[11px] uppercase tracking-wider font-bold text-tac-dim hover:text-tac-primary"
            >
              IMSR (PDF) <ArrowSquareOut size={11} />
            </a>
          </div>
    </div>
  );

  const plBadge = pl != null && (
    <span
      data-testid="nifc-pl-badge"
      className="px-1.5 py-0.5 text-[11px] font-bold uppercase tracking-wider rounded-sm border shrink-0"
      style={{ color: plColor, borderColor: `${plColor}55`, background: `${plColor}15` }}
    >
      PL {pl}
    </span>
  );

  return (
    <section ref={sectionRef} data-testid="nifc-briefing-card" className="border-b border-tac-border bg-tac-surface/40">
      <div className="flex items-center">
        <button
          type="button"
          data-testid="nifc-briefing-toggle"
          onClick={() => setOpen((v) => !v)}
          className="flex-1 min-w-0 flex items-center justify-between gap-2 px-4 py-2.5 text-left hover:bg-tac-surface/60 transition-colors"
        >
          <div className="flex items-center gap-2 min-w-0 flex-wrap">
            <Newspaper size={14} weight="fill" className="text-tac-primary" />
            <span className="text-[12px] uppercase tracking-[0.15em] font-bold text-tac-text">
              NIFC Morning Briefing
            </span>
            {plBadge}
          </div>
          {open ? <CaretUp size={12} className="text-tac-dim shrink-0" /> : <CaretDown size={12} className="text-tac-dim shrink-0" />}
        </button>
        <div className="pr-3 shrink-0 flex items-center gap-1.5">
          <button
            type="button"
            data-testid="nifc-popout-btn"
            title="Open in large view"
            onClick={() => setPopout(true)}
            className="inline-flex items-center justify-center w-7 h-7 border border-tac-border hover:border-tac-primary hover:text-tac-primary text-tac-dim rounded-sm transition-colors"
          >
            <ArrowsOutSimple size={12} weight="bold" />
          </button>
          <InlineShareButton
            variant="compact"
            testid="nifc-share-btn"
            url={`${shareOrigin()}/?brief=nifc`}
            title="NIFC Morning Briefing"
            text={caption}
            caption={caption}
          />
        </div>
      </div>

      {open && <div data-testid="nifc-briefing-body">{body}</div>}

      <BriefPopout open={popout} onClose={() => setPopout(false)} title="NIFC Morning Briefing" badge={plBadge}>
        {body}
      </BriefPopout>
    </section>
  );
}
